import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, LayoutDashboard, LineChart, Video, MessageSquareText, Award, Calendar, Megaphone, Wallet, UserCircle, Dumbbell, ClipboardCheck, FileText, Settings, ShieldCheck } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PortalLayout, SectionTitle, Panel, EmptyState } from "@/components/platform/PortalLayout";
import ProgressCharts from "@/components/platform/ProgressCharts";
import AthleteVideos from "@/components/platform/AthleteVideos";
import MemberBilling from "@/components/platform/MemberBilling";
import MyProfile from "@/components/academy/MyProfile";
import AcademyOverview from "@/components/academy/AcademyOverview";
import PersonalRecords from "@/components/academy/PersonalRecords";
import Achievements from "@/components/academy/Achievements";
import Resources from "@/components/academy/Resources";
import DashboardHeader from "@/components/academy/DashboardHeader";
import OnboardingChecklist from "@/components/academy/OnboardingChecklist";
import OverviewCards from "@/components/academy/OverviewCards";
import AccountSettings from "@/components/academy/AccountSettings";
import MySchedule from "@/components/academy/MySchedule";
import MyDocuments from "@/components/academy/MyDocuments";
import { Empty, fmtDate } from "@/components/platform/athlete/shared";

const ALL_TABS = [
  { key: "overview", label: "Dashboard", icon: LayoutDashboard },
  { key: "training", label: "My Training", icon: Dumbbell },
  { key: "schedule", label: "My Schedule", icon: Calendar },
  { key: "evaluations", label: "Evaluations & Results", icon: ClipboardCheck },
  { key: "progress", label: "My Progress", icon: LineChart },
  { key: "achievements", label: "Achievements", icon: Award },
  { key: "profile", label: "My Athletic Profile", icon: UserCircle },
  { key: "messages", label: "Coach Messages", icon: MessageSquareText },
  { key: "membership", label: "My Membership", icon: Wallet },
  { key: "media", label: "My Media", icon: Video },
  { key: "documents", label: "My Documents", icon: FileText },
  { key: "settings", label: "Account Settings", icon: Settings },
];
const TYPE_TABS = {
  free_evaluation: ["overview", "schedule", "evaluations", "profile", "messages", "membership", "media", "documents", "settings"],
  in_person: ["overview", "schedule", "evaluations", "progress", "achievements", "profile", "messages", "membership", "media", "documents", "settings"],
  online: ["overview", "training", "schedule", "evaluations", "progress", "achievements", "profile", "messages", "membership", "media", "documents", "settings"],
  hybrid: ["overview", "training", "schedule", "evaluations", "progress", "achievements", "profile", "messages", "membership", "media", "documents", "settings"],
  digital: ["overview", "training", "schedule", "evaluations", "progress", "achievements", "profile", "messages", "membership", "media", "documents", "settings"],
};

export default function AthleteDashboard() {
  const { user } = useAuth();
  const [tab, setTab] = useState("overview");
  const [athlete, setAthlete] = useState(null);
  const [dash, setDash] = useState(null);
  const [d, setD] = useState({});
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data: aths } = await api.get("/athletes");
      const a = aths[0] || null;
      setAthlete(a);
      if (!a) { setLoading(false); return; }
      const id = a.id;
      const [dashboard, snap, recs, prs, sp, perf, ach, feedback, docs, resources, vids, anns, sess] = await Promise.all([
        api.get(`/athletes/${id}/dashboard`), api.get(`/athletes/${id}/snapshot`),
        api.get(`/test-records?athlete_id=${id}`), api.get(`/athletes/${id}/records`),
        api.get(`/athletes/${id}/speed-points`), api.get(`/athletes/${id}/performance`),
        api.get(`/athletes/${id}/achievements`), api.get(`/feedback?athlete_id=${id}`),
        api.get(`/documents?athlete_id=${id}`), api.get("/resources"),
        api.get(`/videos?athlete_id=${id}`), api.get("/announcements"), api.get("/sessions"),
      ]);
      setDash(dashboard.data);
      setD({ snapshot: snap.data, records: recs.data, prs: prs.data, sp: sp.data, perf: perf.data,
             achievements: ach.data, feedback: feedback.data, docs: docs.data, resources: resources.data,
             videos: vids.data, announcements: anns.data, sessions: sess.data });
    } catch { toast.error("Failed to load Academy."); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const reloadVideos = useCallback(async () => {
    if (athlete) { const { data } = await api.get(`/videos?athlete_id=${athlete.id}`); setD((p) => ({ ...p, videos: data })); }
  }, [athlete]);

  if (loading) return <PortalLayout roleLabel="Origin Speed Academy"><div className="flex justify-center py-24 text-white/40"><Loader2 className="animate-spin" /></div></PortalLayout>;

  if (!athlete) return (
    <PortalLayout roleLabel="Origin Speed Academy">
      <p className="font-mono-accent text-xs tracking-[0.3em] uppercase text-white/40 mb-2">Welcome to Origin Speed Academy</p>
      <h1 className="font-display font-black uppercase tracking-tighter text-4xl md:text-5xl mb-6">{user?.name || "Athlete"}</h1>
      <EmptyState testid="no-athlete-profile">Your athlete profile is being set up. Please check back shortly or contact your coach.</EmptyState>
    </PortalLayout>
  );

  const type = athlete.athlete_type || "in_person";
  const visible = ALL_TABS.filter((t) => (TYPE_TABS[type] || TYPE_TABS.in_person).includes(t.key));
  const reports = (d.docs || []).filter((x) => x.category === "Progress Report");
  const lvl = dash?.level || {};

  return (
    <PortalLayout roleLabel="Origin Speed Academy">
      <DashboardHeader dash={dash} />

      <div className="flex gap-1 border-b border-white/10 mb-8 overflow-x-auto" data-testid="academy-tabs">
        {visible.map((t) => {
          const Icon = t.icon;
          return (
            <button key={t.key} data-testid={`academy-tab-${t.key}`} onClick={() => setTab(t.key)} className={`flex items-center gap-2 px-4 py-3 text-xs uppercase tracking-widest font-bold whitespace-nowrap border-b-2 transition-colors ${tab === t.key ? "border-white text-white" : "border-transparent text-white/40 hover:text-white"}`}>
              <Icon size={14} /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "overview" && (
        <div className="space-y-8" data-testid="academy-overview-tab">
          <OverviewCards dash={dash} onNav={setTab} />
          {(dash?.onboarding || []).some((o) => !o.done) && <OnboardingChecklist items={dash.onboarding} onNav={setTab} />}
          <AcademyOverview snapshot={d.snapshot} performance={d.perf} speedPoints={d.sp} />
          <div>
            <SectionTitle>Announcements</SectionTitle>
            {(d.announcements || []).length ? <div className="space-y-3" data-testid="athlete-announcements">{d.announcements.map((a) => (
              <Panel key={a.id}><div className="flex items-center gap-2 mb-1"><Megaphone size={14} className="text-white/50" /><p className="font-bold text-sm">{a.title}</p></div><p className="text-white/60 text-sm">{a.body}</p></Panel>
            ))}</div> : <EmptyState testid="athlete-no-announcements">No announcements right now.</EmptyState>}
          </div>
        </div>
      )}

      {tab === "training" && (
        <div data-testid="academy-training">
          <SectionTitle>My Training</SectionTitle>
          <EmptyState testid="training-empty">Your coach has not assigned a workout program yet. Assigned workouts, exercises and demonstration videos will appear here.</EmptyState>
        </div>
      )}

      {tab === "schedule" && <MySchedule sessions={d.sessions} nextRetest={athlete.next_retest_date} />}

      {tab === "evaluations" && (
        <div data-testid="academy-evaluations" className="space-y-8">
          <div><SectionTitle>Personal Records & Results</SectionTitle><PersonalRecords records={d.prs} /></div>
          <div>
            <SectionTitle>Coach Feedback</SectionTitle>
            {(d.feedback || []).length === 0 ? <Empty testid="academy-feedback-empty">No evaluation feedback yet.</Empty> : (
              <div className="space-y-4">
                {d.feedback.map((f) => (
                  <div key={f.id} data-testid={`academy-feedback-${f.id}`} className="bg-[#121212] border border-white/10 p-5">
                    <div className="flex items-center justify-between mb-2"><p className="font-display font-bold uppercase tracking-tight">{f.focus || "Session"}</p><p className="text-white/40 text-xs">{fmtDate(f.session_date || f.created_at)}</p></div>
                    <div className="grid sm:grid-cols-2 gap-3 text-sm">
                      {f.strengths && <div><p className="font-mono-accent text-[10px] tracking-[0.2em] uppercase text-white/40 mb-1">Strengths</p><p className="text-white/75">{f.strengths}</p></div>}
                      {f.improvements && <div><p className="font-mono-accent text-[10px] tracking-[0.2em] uppercase text-white/40 mb-1">Improve</p><p className="text-white/75">{f.improvements}</p></div>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          {reports.length > 0 && (
            <div><SectionTitle>Evaluation Reports</SectionTitle><MyDocuments docs={reports} /></div>
          )}
        </div>
      )}

      {tab === "progress" && <div data-testid="academy-progress"><SectionTitle>My Progress</SectionTitle><ProgressCharts records={d.records} /></div>}

      {tab === "achievements" && (
        <div data-testid="academy-achievements-tab" className="space-y-6">
          <SectionTitle>Achievements</SectionTitle>
          <Panel data-testid="achievements-level">
            <div className="flex items-center gap-3 mb-3"><ShieldCheck size={20} className="text-white/60" /><div><p className="font-display font-black uppercase tracking-tight text-2xl leading-none">{lvl.name || "Rookie"}</p><p className="text-white/40 text-[10px] uppercase tracking-widest mt-1">Athlete Level · {dash?.speed_points?.lifetime ?? 0} SP</p></div></div>
            <div className="w-full h-2 bg-white/10"><div className="h-full bg-white" style={{ width: `${lvl.progress_pct ?? 0}%` }} /></div>
            <p className="text-white/40 text-xs mt-2">{lvl.next_name ? `${lvl.sp_to_next} Speed Points to reach ${lvl.next_name}` : "You've reached the top level!"}</p>
          </Panel>
          <Achievements achievements={d.achievements} />
        </div>
      )}

      {tab === "profile" && <MyProfile athlete={athlete} accountEmail={dash?.account?.email} onSaved={load} />}

      {tab === "messages" && (
        <div data-testid="academy-messages">
          <SectionTitle>Coach Messages</SectionTitle>
          <EmptyState testid="messages-empty">You have no messages yet. Messages, feedback and reminders from your coach will appear here.</EmptyState>
        </div>
      )}

      {tab === "membership" && <div data-testid="academy-membership"><SectionTitle>My Membership</SectionTitle><MemberBilling /></div>}

      {tab === "media" && <div data-testid="academy-video"><SectionTitle>My Media</SectionTitle><AthleteVideos athleteId={athlete.id} videos={d.videos} onChange={reloadVideos} /></div>}

      {tab === "documents" && (
        <div className="space-y-8">
          <MyDocuments docs={d.docs} />
          <div><SectionTitle>Resources & Guides</SectionTitle><Resources resources={d.resources} /></div>
        </div>
      )}

      {tab === "settings" && <AccountSettings athlete={athlete} account={dash?.account} onSaved={load} />}
    </PortalLayout>
  );
}
